import React, { lazy, Suspense, Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Switch, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { debounce, find } from 'lodash';

import { MuiThemeProvider } from '@material-ui/core/styles';
import MobileAppEntry from 'pages/Home/MobileAppEntry';
import Register from 'pages/Register/Register';
import FinishPaidRegistration from 'pages/Register/FinishPaidRegistration';
import InternationalCustomersRegistration from 'pages/Register/InternationalCustomersRegistration';
import FinishCustomProductRegistration from 'pages/Register/CustomProductRegistration/FinishCustomProductRegistration';
import Login from 'pages/Login/Login';
import API from 'api';

import LoginClever from 'pages/LoginClever/LoginClever';
import GuestActivityPreview from 'pages/GuestActivityPreview';

import NotFoundPage from 'pages/NotFoundPage/NotFoundPage';

import { Loader, Dialogs, Spinner, MobileRuntimeDebug } from 'components/common';
import MobileUpdateAvailable from 'components/common/MobileUpdateAvailable/MobileUpdateAvailable';
import { isOnline } from 'mobile-modules/offline';
import RefreshScreen from 'components/common/RefreshScreen';
import { renderRoutes, setAuthToken, muiTheme } from 'utils';
import { initializeMobilePushBridge } from 'utils/mobileNativeBridge';
import { startMobilePushModule, syncMobilePushDevice } from 'mobile-modules/push-notifications';
import {
  getAndroidTapDiagnosticSnapshot,
  startAndroidTapDiagnostics,
  stopAndroidTapDiagnostics,
} from 'mobile-modules/tap-diagnostics';
import { logout, updateToken } from 'actions/authActions';
import { getCommonData } from 'actions/commonActions';
import { changeLocale, setAspectRatio } from 'actions/statusActions';
import store from 'store';
import { allRoles } from 'consts/user-roles';
import isMobileAppRuntime from 'utils/isMobileAppRuntime';
import {
  MOBILE_FAMILY_ACCOUNT_TOKEN_KEY,
  MOBILE_FAMILY_STATE_RESET_REQUIRED,
  MOBILE_FAMILY_STATE_SUSPENDED,
  clearMobileFamilyForRegistration,
  clearMobileLearnerSession,
  getMobileFamilyState,
} from 'utils/mobileFamilyAccess';

import './utils/audioPlayer';
import './settings';
import 'normalize.css';
import 'animate.css';
import 'react-datepicker/dist/react-datepicker.css';

const Admin = lazy(() => import('pages/Admin/Admin'));
const FlinkAdmin = lazy(() => import('pages/FlinkAdmin/FlinkAdmin'));
const FlinkPlay = lazy(() => import('pages/FlinkPlay/FlinkPlay'));
const FlinkMake = lazy(() => import('pages/FlinkMake/FlinkMake'));

const routes = [
  ['/', true, MobileAppEntry],
  ['/register-by-code/:code', true, Register],
  ['/finish-registration/:pendingRegistration', true, FinishPaidRegistration],
  ['/international-customers/:pendingRegistration', true, InternationalCustomersRegistration],
  ['/finish-custom-registration/:pendingRegistration', true, FinishCustomProductRegistration],
  ['/login', true, Login],
  ['/login/clever', true, LoginClever],
  ['/admin', false, Admin, allRoles],
  ['/flink-admin', false, FlinkAdmin, { apps: { flinkAdmin: true } }],
  ['/flink-make', false, FlinkMake, { apps: { flinkMake: true } }],
  ['/play', true, FlinkPlay, 'learner'],

  ['/:learningCenterUrl', true, Login],

  // preview activity as guest
  ['/preview-activity/:activityId', true, GuestActivityPreview],

  // 404 Page
  [null, false, NotFoundPage],
];

class App extends Component {
  constructor(props) {
    super(props);

    // Check for token
    if (localStorage.jwtToken) {
      // Set token from local storage
      try {
        setAuthToken(localStorage.jwtToken);
        // Update token from server
        updateToken(localStorage.jwtToken)(store.dispatch);
      } catch (err) {
        localStorage.removeItem('jwtToken');
      }
    }

    this.state = { redirectTo: '/' };
  }

  componentDidMount() {
    initializeMobilePushBridge();
    startMobilePushModule();
    startAndroidTapDiagnostics();
    this.startMobileFamilyAccessMonitor();
    if (isMobileAppRuntime()) {
      window.addEventListener('flc:offline-online-returned', this.handleMobileOnlineReturn);
    }
    this.init();
  }

  componentWillUnmount() {
    stopAndroidTapDiagnostics();
    if (this.mobileFamilyAccessTimer) window.clearInterval(this.mobileFamilyAccessTimer);
    document.removeEventListener('visibilitychange', this.handleMobileFamilyVisibilityChange);
    window.removeEventListener('flc:offline-online-returned', this.handleMobileOnlineReturn);
  }

  handleMobileFamilyVisibilityChange = () => {
    if (!document.hidden) this.checkMobileFamilyAccess();
  };

  handleMobileOnlineReturn = () => {
    if (!isMobileAppRuntime() || !isOnline()) return;
    const token = localStorage.jwtToken;
    if (token && !String(token).endsWith('.offline')) {
      updateToken(token)(store.dispatch);
    }
    this.checkMobileFamilyAccess();
  };

  startMobileFamilyAccessMonitor = () => {
    if (!isMobileAppRuntime()) return;
    this.checkMobileFamilyAccess();
    this.mobileFamilyAccessTimer = window.setInterval(this.checkMobileFamilyAccess, 10000);
    document.addEventListener('visibilitychange', this.handleMobileFamilyVisibilityChange);
  };

  checkMobileFamilyAccess = async () => {
    if (!isMobileAppRuntime() || this.mobileFamilyAccessChecking) return;
    const familyAccountToken = localStorage.getItem(MOBILE_FAMILY_ACCOUNT_TOKEN_KEY) || '';
    if (!familyAccountToken) return;

    this.mobileFamilyAccessChecking = true;
    try {
      const session = await API.mobileWrapper.getFamilySession(
        familyAccountToken,
        getAndroidTapDiagnosticSnapshot(),
      );
      if (!session?.success) {
        if (Number(session?.statusCode) === 404 || /mobile family account was not found/i.test(String(session?.error || ''))) {
          clearMobileFamilyForRegistration();
          store.dispatch(logout());
          window.location.replace('/');
        }
        return;
      }

      const state = getMobileFamilyState(session);
      const suspendedMarker = window.location.pathname === '/'
        && new URLSearchParams(window.location.search || '').get('mobileFamily') === 'suspended';

      if (state === MOBILE_FAMILY_STATE_RESET_REQUIRED) {
        clearMobileFamilyForRegistration();
        store.dispatch(logout());
        window.location.replace('/');
        return;
      }

      if (state === MOBILE_FAMILY_STATE_SUSPENDED) {
        clearMobileLearnerSession();
        store.dispatch(logout());
        if (!suspendedMarker) window.location.replace('/?mobileFamily=suspended');
        return;
      }

      if (suspendedMarker) window.location.replace('/');
    } finally {
      this.mobileFamilyAccessChecking = false;
    }
  };

  componentDidUpdate(prevProps) {
    const { learner, auth } = this.props;
    const learnerOrUserChanged = prevProps.learner !== learner || prevProps.auth.user !== auth.user;

    if (
      prevProps.auth.isAuthenticated !== auth.isAuthenticated
      || prevProps.auth.learner !== auth.learner
      || learnerOrUserChanged
    ) {
      syncMobilePushDevice();
    }

    if (learnerOrUserChanged) {
      /*
      If the learner (flink play) or the user (flink admin) has changed, we should update redirect which will be fired
      if the user doesn't have enough permissions to view some page (see CustomRoute.js)

      The renderRoute function takes care of rendering the CustomRoute component and passing
      that log-out redirect URI to it
      */

      // this.setLanguage();
      this.setRedirectTo();
    }
  }

  init = async () => {
    const { getCommonData } = this.props;

    if (this.state.error) {
      this.setState({ error: '' });
    }

    const success = await getCommonData();

    if (!success) {
      return this.setState({
        error: isMobileAppRuntime() && !isOnline()
          ? 'Internet connection is required for the initial setup on this device.'
          : 'Error connecting to server.',
      });
    }

    this.checkAspectRatio();
    window.addEventListener('resize', debounce(this.checkAspectRatio, 100));

    this.setLanguage();

    /*stage*/
    const { common } = this.props;
    if (Array.isArray(common?.locales) && common.locales.length > 0) {
      this.setLanguage();
    } else {
      console.warn('[App.init] locales not ready after getCommonData');
    }
    /*end stage*/

    this.setRedirectTo();
  };

  setRedirectTo = () => {
    /*
    Look at line 80 comment!
    */

    const { learner, auth, common } = this.props;

    if (!common) return;

    const { learningCenters } = common;

    const isCleverLogin = auth.cleverLogin;
    const learningCenterId =
      (learner && learner.learningCenterId) ||
      (auth.user && auth.user.learningCenterId);

    let redirectTo = '/';

    if (isCleverLogin) {
      /* 
      If we're logged in with clever, our log-out redirect must be clever website
      */

      redirectTo = new URL('https://clever.com'); // look at comment in the CustomRoute
    } else if (learningCenterId) {
      const learningCenter = find(learningCenters, { _id: learningCenterId });

      if (learningCenter && learner) {
        const familyParams = new URLSearchParams({ family: learner.familyId });
        redirectTo = `${learningCenter.url}?${familyParams.toString()}`;
        // redirectTo = learningCenter.url;
      }
    }

    console.log(`Setting logout redirect to ${redirectTo.toString()}`);
    this.setState({ redirectTo });
  };

  checkAspectRatio = () => {
    const { setAspectRatio } = this.props;
    const { innerWidth, innerHeight } = window;
    const aspectRatio = innerWidth / innerHeight;

    setAspectRatio(aspectRatio);
  };

  setLanguage = () => {
    const { changeLocale } = this.props;

    // Set language from Local Storage if exists
    const localeCode = localStorage.getItem('localeCode') || 'en';
    changeLocale(localeCode);
  };

  render() {
    const { auth, common } = this.props;
    const { error, redirectTo } = this.state;

    if (error) {
      return (
        <RefreshScreen
          refreshHandler={this.init}
          text={error}
        />
      );
    }

    return (
      <MuiThemeProvider theme={muiTheme}>
        <Loader />
        <MobileRuntimeDebug />
        <MobileUpdateAvailable />

        {common && <Dialogs />}

        <Suspense fallback={<Spinner />}>
          {common && <Switch>{renderRoutes(routes, auth, redirectTo)}</Switch>}
        </Suspense>
      </MuiThemeProvider>
    );
  }
}

App.propTypes = {
  getCommonData: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  learner: PropTypes.object,
};

const mapStateToProps = ({ auth, common, flinkPlay }) => ({
  auth,
  learner: flinkPlay.learner,
  common,
});

export default compose(
  withRouter,
  connect(mapStateToProps, {
    getCommonData,
    changeLocale,
    setAspectRatio,
  })
)(App);
